import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Image, TouchableOpacity, View, Text } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import ProgressBar from "react-native-progress/Bar";
import { useDropzone } from "react-dropzone";
import tw from "twrnc";

import { coverUpload } from "./EventPersonalizeUploadUtils";
import { resetThumbnail } from "../http/events";
import { t } from "@/localization";

/**
 * Web-specific version of ChangeCoverImageButton with drag-and-drop functionality
 */
const ChangeCoverImageButtonWeb = ({
  event,
  onComplete,
  style,
  imageWidth = 300,
  showHoverMessage = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0.0);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [imageAspectRatio, setImageAspectRatio] = useState(16 / 9);
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveringButtons, setIsHoveringButtons] = useState(false);
  const uploadCompletedRef = useRef(false);
  const coverPathAtUploadRef = useRef(null);

  const { event_cover_path } = event;

  // Check if a custom photo is uploaded
  const photoUploaded =
    event_cover_path &&
    event_cover_path !==
      "https://eventliveprod-posters.s3.amazonaws.com/event-live-default.jpg";

  // Calculate image dimensions and aspect ratio
  useEffect(() => {
    if (event_cover_path) {
      Image.getSize(
        event_cover_path,
        (w, h) => {
          if (w > 0 && h > 0) {
            setImageAspectRatio(w / h);
          } else {
            setImageAspectRatio(16 / 9);
          }
        },
        (err) => {
          console.error("Failed to get image size:", err);
          setImageAspectRatio(16 / 9);
        }
      );
    } else {
      setImageAspectRatio(16 / 9);
    }
  }, [event_cover_path]);

  // When event_cover_path changes after upload, processing is done
  useEffect(() => {
    if (
      processing &&
      coverPathAtUploadRef.current &&
      event_cover_path !== coverPathAtUploadRef.current
    ) {
      setProcessing(false);
      coverPathAtUploadRef.current = null;
    }
  }, [event_cover_path, processing]);

  const completeUpload = useCallback(() => {
    if (uploadCompletedRef.current) {
      return;
    }

    uploadCompletedRef.current = true;
    setProgress(1);
    setLoading(false);
    setProcessing(true);

    if (typeof onComplete === "function") {
      onComplete({});
    }
  }, [onComplete]);

  const onRequestProgress = useCallback(
    (progressEvent) => {
      console.log("PROGRESS EVENT:", progressEvent);
      if (typeof progressEvent.progress === "number") {
        setProgress(progressEvent.progress);
        if (progressEvent.progress >= 1) {
          completeUpload();
        }
      } else {
        setLoaded(progressEvent.loaded || progressEvent.transferredBytes || 0);
        setTotal(progressEvent.total || progressEvent.totalBytes || 0);
        const calculatedProgress =
          (progressEvent.loaded || progressEvent.transferredBytes || 0) /
          (progressEvent.total || progressEvent.totalBytes || 1);
        setProgress(isNaN(calculatedProgress) ? 0 : calculatedProgress);
        if (calculatedProgress >= 1) {
          completeUpload();
        }
      }
    },
    [completeUpload]
  );

  const uploadImageFile = async (file, filename) => {
    uploadCompletedRef.current = false;
    coverPathAtUploadRef.current = event_cover_path;
    setLoading(true);
    setProcessing(false);
    setProgress(0);
    setLoaded(0);
    setTotal(0);

    try {
      await coverUpload({
        event,
        file,
        filename,
        onProgress: onRequestProgress,
      });
      completeUpload();
    } catch (error) {
      console.error("Upload failed:", error);
      Alert.alert(t("Upload failed"), t("Please try again"));
      uploadCompletedRef.current = false;
      setLoading(false);
    }
  };

  // Web dropzone functionality
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const file = acceptedFiles[0];
      await uploadImageFile(file, file.name);
    },
    [event]
  );

  const onDropRejected = useCallback(() => {
    Alert.alert(t("Invalid file"), t("Only JPG or PNG files are accepted"));
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    multiple: false,
    accept: "image/*",
    disabled: loading,
  });

  const handlePress = () => {
    open();
  };

  const imageHeight = imageWidth / imageAspectRatio;

  const handleDeleteCover = async () => {
    if (window.confirm(t("Delete Cover?"))) {
      try {
        await resetThumbnail(event);
        if (typeof onComplete === "function") {
          onComplete({});
        }
      } catch (error) {
        console.error("Failed to delete cover:", error);
        Alert.alert(t("Delete failed"), t("Please try again"));
      }
    }
  };

  return (
    <div
      {...getRootProps()}
      style={{ position: "relative", ...style }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input {...getInputProps()} style={{ display: "none" }} />

      <TouchableOpacity
        onPress={handlePress}
        style={tw`relative`}
        disabled={loading || processing}
      >
        <Image
          source={{ uri: event_cover_path }}
          style={[
            tw`rounded-lg border border-gray-200`,
            {
              width: imageWidth,
              height: imageHeight,
            },
          ]}
        />

        {/* Loading overlay */}
        {(loading || processing) && (
          <View
            style={[
              tw`absolute inset-0 bg-black/50 items-center justify-center rounded-lg`,
            ]}
          >
            <View style={tw`bg-white p-6 rounded-lg shadow-lg`}>
              <ProgressBar
                progress={processing ? 0 : progress}
                indeterminate={processing || progress === 0}
                color="#F84123"
                width={200}
                style={tw`mb-3`}
              />
              <Text style={tw`text-center text-sm font-medium`}>
                {processing
                  ? t("Processing image...")
                  : t("Uploading... {percent}%", {
                      percent: Math.round(progress * 100),
                    })}
              </Text>
            </View>
          </View>
        )}

        {/* Web hover message */}
        {showHoverMessage && !loading && !processing && (
          <View
            style={[
              tw`absolute inset-0 items-center justify-center rounded-lg`,
              {
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                opacity:
                  (isHovered && !isHoveringButtons) || isDragActive ? 1 : 0,
                transition: "opacity 0.2s ease-in-out",
                pointerEvents:
                  (isHovered && !isHoveringButtons) || isDragActive
                    ? "auto"
                    : "none",
              },
            ]}
          >
            <View style={tw`bg-white p-4 rounded-lg mx-4`}>
              <Text style={tw`text-center text-sm font-medium mb-2`}>
                {isDragActive
                  ? t("Drop image here")
                  : t("Drop image here or click to select")}
              </Text>
              <Text style={tw`text-center text-xs text-gray-600`}>
                {t("Recommended size: 1280x720 pixels")}
              </Text>
              <Text style={tw`text-center text-xs text-gray-600`}>
                {t("Accepts PNG or JPG under 50MB")}
              </Text>
            </View>
          </View>
        )}

        {/* Change Cover Button Overlay */}
        <View
          style={[tw`absolute top-3 right-3 flex-row items-center`]}
          onMouseEnter={() => setIsHoveringButtons(true)}
          onMouseLeave={() => setIsHoveringButtons(false)}
        >
          {/* Change Cover Button */}
          <TouchableOpacity
            onPress={handlePress}
            disabled={loading}
            style={[
              tw`flex-row items-center rounded-lg mr-2`,
              {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                paddingHorizontal: 8,
                paddingVertical: 6,
              },
            ]}
          >
            <Ionicons name="camera" size={18} color="white" style={tw`mr-1`} />
            <Text style={[tw`text-white text-sm`]}>{t("Change")}</Text>
          </TouchableOpacity>

          {/* Delete Cover Button - only show if custom photo is uploaded */}
          {photoUploaded && (
            <TouchableOpacity
              onPress={handleDeleteCover}
              disabled={loading}
              style={[
                tw`flex-row items-center rounded-lg`,
                {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  paddingHorizontal: 8,
                  paddingVertical: 6,
                },
              ]}
            >
              <Ionicons name="trash" size={18} color="white" />
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    </div>
  );
};

export default ChangeCoverImageButtonWeb;
